import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { boqAPI, jobsAPI, usersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format } from 'date-fns';
import JobSearchSelect from '../components/JobSearchSelect';
import * as XLSX from 'xlsx';
import {
  ClipboardList,
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Package,
  FileText,
  Calculator,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  AlertTriangle,
  ShoppingCart,
  Upload,
  Download,
  Link2,
  Lock,
} from 'lucide-react';

const BOQManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(jobId || '');
  const [existingBOQ, setExistingBOQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [approverName, setApproverName] = useState('');
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [importing, setImporting] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [requisitioning, setRequisitioning] = useState(false);

  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const canApprove = user?.role === 'admin' || user?.role === 'manager';

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectedItems.size === lineItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(lineItems.map(item => item.id)));
    }
  };

  // Toggle single item
  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Delete selected items
  const deleteSelectedItems = () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.size} selected item(s)?`)) return;
    
    setLineItems(lineItems.filter(item => !selectedItems.has(item.id)));
    setSelectedItems(new Set());
    toast.success(`${selectedItems.size} item(s) deleted`);
  };

  // Requisition selected items
  const requisitionSelectedItems = async () => {
    if (selectedItems.size === 0) return;
    if (!existingBOQ) return;
    
    // Check if any selected item is already requisitioned
    const selectedItemsList = lineItems.filter(item => selectedItems.has(item.id));
    const alreadyRequisitioned = selectedItemsList.filter(item => item.requisitioned);
    
    if (alreadyRequisitioned.length > 0) {
      toast.error(`${alreadyRequisitioned.length} item(s) are already requisitioned`);
      return;
    }
    
    setRequisitioning(true);
    try {
      await boqAPI.requisitionItems(existingBOQ.id, Array.from(selectedItems));
      toast.success(`${selectedItems.size} item(s) marked as requisitioned`);
      setSelectedItems(new Set());
      loadExistingBOQ();
    } catch (error) {
      toast.error('Failed to requisition items');
    } finally {
      setRequisitioning(false);
    }
  };

  // Check if selection has non-requisitioned items
  const hasNonRequisitionedSelected = () => {
    return lineItems.some(item => selectedItems.has(item.id) && !item.requisitioned);
  };

  useEffect(() => {
    loadJobs();
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedJob) {
      loadExistingBOQ();
    } else {
      setExistingBOQ(null);
      setLineItems([]);
    }
  }, [selectedJob]);

  const loadJobs = async () => {
    try {
      const response = await jobsAPI.getAll();
      setJobs(response.data);
      if (jobId) {
        setSelectedJob(jobId);
      }
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await boqAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      // Default categories if API fails
      setCategories(['Labour', 'Materials', 'Plant & Equipment', 'Sub-Contractor', 'Preliminaries', 'Contingency', 'Professional Fees', 'Other']);
    }
  };

  const loadExistingBOQ = async () => {
    try {
      const response = await boqAPI.getJobBOQ(selectedJob);
      if (response.data) {
        setExistingBOQ(response.data);
        setLineItems(response.data.line_items || []);
        // Load approver name if approved
        if (response.data.approved_by) {
          try {
            const userRes = await usersAPI.getOne(response.data.approved_by);
            setApproverName(userRes.data.name);
          } catch (e) {
            setApproverName('Unknown');
          }
        }
      }
    } catch (error) {
      // No existing BOQ - that's fine
      setExistingBOQ(null);
      setLineItems([]);
    }
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        id: Date.now().toString(),
        category: 'Materials',
        item_description: '',
        quantity: 1,
        unit: 'each',
        rate: 0,
        amount: 0,
        specifications: '',
      },
    ]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Auto-calculate amount
    if (field === 'quantity' || field === 'rate') {
      updated[index].amount = updated[index].quantity * updated[index].rate;
    }
    
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const calculateByCategory = () => {
    const categoryTotals = {};
    lineItems.forEach(item => {
      const cat = item.category || 'Other';
      categoryTotals[cat] = (categoryTotals[cat] || 0) + (item.amount || 0);
    });
    return categoryTotals;
  };

  // Excel Import Functions
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Skip header row and parse data
      // Expected columns: NO, DESCRIPTION, UNIT, QTY, RATE, AMOUNT
      const importedItems = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) continue;

        const no = row[0];
        const description = row[1] || '';
        const unit = row[2] || '';
        const qty = parseFloat(row[3]) || 0;
        const rate = parseFloat(row[4]) || 0;
        const amount = parseFloat(row[5]) || (qty * rate);

        // Skip completely empty rows
        if (!description && !unit && qty === 0 && rate === 0) continue;

        // Check if this is a descriptive-only item (no qty, unit, rate, amount)
        const isDescriptiveItem = !unit && qty === 0 && rate === 0;

        importedItems.push({
          id: Date.now().toString() + i,
          category: 'Other', // Default category
          item_description: description,
          quantity: isDescriptiveItem ? 0 : qty,
          unit: isDescriptiveItem ? '-' : (unit || 'each'),
          rate: isDescriptiveItem ? 0 : rate,
          amount: isDescriptiveItem ? 0 : amount,
          specifications: isDescriptiveItem ? 'Descriptive item' : '',
          item_no: no ? String(no) : '',
        });
      }

      if (importedItems.length === 0) {
        toast.error('No valid items found in the file. Please check the format.');
        return;
      }

      // Add imported items to existing line items
      setLineItems([...lineItems, ...importedItems]);
      toast.success(`Successfully imported ${importedItems.length} items`);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import file. Please check the format.');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['NO', 'DESCRIPTION', 'UNIT', 'QTY', 'RATE', 'AMOUNT'],
      ['1', 'Example: Concrete work for foundation', 'm³', '10', '2500', '25000'],
      ['2', 'Example: Roof tiles installation', 'm²', '50', '350', '17500'],
      ['3', 'Section Header: PRELIMINARY WORKS', '', '', '', ''],
      ['4', 'Site clearance and preparation', 'sum', '1', '5000', '5000'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 8 },   // NO
      { wch: 50 },  // DESCRIPTION
      { wch: 10 },  // UNIT
      { wch: 10 },  // QTY
      { wch: 12 },  // RATE
      { wch: 15 },  // AMOUNT
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BOQ Template');
    XLSX.writeFile(wb, 'BOQ_Import_Template.xlsx');
    toast.success('Template downloaded');
  };

  const handleSave = async () => {
    if (!selectedJob) {
      toast.error('Please select a job');
      return;
    }

    if (lineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    // Validate line items
    for (const item of lineItems) {
      if (!item.item_description) {
        toast.error('All items must have a description');
        return;
      }
    }

    setSaving(true);
    try {
      const data = {
        job_id: selectedJob,
        line_items: lineItems.map(item => ({
          ...item,
          amount: item.quantity * item.rate,
        })),
      };

      if (existingBOQ) {
        await boqAPI.update(existingBOQ.id, { line_items: data.line_items });
        toast.success('BOQ updated successfully');
      } else {
        await boqAPI.create(data);
        toast.success('BOQ created successfully');
      }
      
      loadExistingBOQ();
    } catch (error) {
      toast.error('Failed to save BOQ');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!existingBOQ) return;
    
    try {
      await boqAPI.submitForApproval(existingBOQ.id);
      toast.success('BOQ submitted for approval. Approvers have been notified.');
      loadExistingBOQ();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const handleApprove = async (approved) => {
    if (!existingBOQ) return;
    
    try {
      await boqAPI.approve(existingBOQ.id, {
        approved,
        rejection_reason: approved ? null : rejectionReason
      });
      toast.success(approved ? 'BOQ approved successfully' : 'BOQ rejected');
      setShowApprovalModal(false);
      setRejectionReason('');
      loadExistingBOQ();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleGenerateRequisition = async () => {
    if (!existingBOQ) return;
    
    try {
      await boqAPI.generateRequisition(existingBOQ.id);
      toast.success('Requisition generated successfully! Materials can now be procured.');
      navigate(`/requisitions?jobId=${selectedJob}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate requisition');
    }
  };

  const selectedJobData = jobs.find(j => j.id === selectedJob);
  const isEditable = existingBOQ?.status === 'draft' || existingBOQ?.status === 'rejected' || !existingBOQ;
  const categoryTotals = calculateByCategory();

  const statusConfig = {
    draft: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText, label: 'Draft' },
    pending_approval: { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock, label: 'Pending Approval' },
    approved: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Approved' },
    rejected: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Rejected' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bill of Quantities
            </h1>
            <p className="text-slate-600">Create and manage BOQ for jobs</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && isEditable && (
            <button
              onClick={handleSave}
              disabled={saving || !selectedJob}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : existingBOQ ? 'Update BOQ' : 'Create BOQ'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Job Selection */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Select Job
            </h2>
            <JobSearchSelect
              jobs={jobs}
              selectedJobId={selectedJob}
              onSelect={setSelectedJob}
              placeholder="Search for a job by code, title, or location..."
              disabled={!!jobId}
            />

            {selectedJobData && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{selectedJobData.title}</p>
                <p className="text-sm text-slate-600">{selectedJobData.location}</p>
                {existingBOQ && (
                  <div className="mt-2 flex items-center gap-2">
                    {(() => {
                      const config = statusConfig[existingBOQ.status] || statusConfig.draft;
                      const Icon = config.icon;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                          {config.label}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rejection Reason (if rejected) */}
          {existingBOQ?.status === 'rejected' && existingBOQ?.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">BOQ Rejected</p>
                <p className="text-sm text-red-600 mt-1">{existingBOQ.rejection_reason}</p>
                <p className="text-xs text-red-500 mt-2">Please update the BOQ and resubmit for approval.</p>
              </div>
            </div>
          )}

          {/* Line Items */}
          {selectedJob && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Line Items
                </h2>
                {canEdit && isEditable && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={downloadTemplate}
                      className="flex items-center gap-2 bg-slate-50 hover:bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg transition-colors text-sm border border-slate-200"
                      title="Download Excel template"
                    >
                      <Download className="w-4 h-4" />
                      Template
                    </button>
                    <button
                      onClick={handleImportClick}
                      disabled={importing}
                      className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-600 px-3 py-1.5 rounded-lg transition-colors text-sm border border-green-200"
                    >
                      <Upload className="w-4 h-4" />
                      {importing ? 'Importing...' : 'Import Excel'}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileImport}
                      accept=".xlsx,.xls,.csv"
                      className="hidden"
                    />
                    <button
                      onClick={addLineItem}
                      className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                )}
              </div>

              {lineItems.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No line items yet.</p>
                  {canEdit && isEditable && (
                    <div className="mt-4 space-y-2">
                      <button
                        onClick={handleImportClick}
                        disabled={importing}
                        className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors mr-3"
                      >
                        <Upload className="w-4 h-4" />
                        {importing ? 'Importing...' : 'Import from Excel'}
                      </button>
                      <button
                        onClick={addLineItem}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add Manually
                      </button>
                      <p className="text-xs text-slate-500 mt-3">
                        <button onClick={downloadTemplate} className="text-blue-600 hover:underline">
                          Download template
                        </button>
                        {' '}with columns: NO, DESCRIPTION, UNIT, QTY, RATE, AMOUNT
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Bulk actions bar - shows when items are selected */}
                  {selectedItems.size > 0 && (
                    <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                      <span className="text-sm text-blue-700">{selectedItems.size} item(s) selected</span>
                      <div className="flex items-center gap-3">
                        {/* Requisition button - only for non-requisitioned items */}
                        {canEdit && existingBOQ && hasNonRequisitionedSelected() && (
                          <button
                            onClick={requisitionSelectedItems}
                            disabled={requisitioning}
                            className="flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-medium disabled:opacity-50"
                            data-testid="requisition-selected-btn"
                          >
                            <ShoppingCart className="w-4 h-4" />
                            {requisitioning ? 'Processing...' : 'Requisition Selected'}
                          </button>
                        )}
                        {/* Delete button - only in edit mode for non-requisitioned items */}
                        {canEdit && isEditable && (
                          <button
                            onClick={deleteSelectedItems}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Selected
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {/* Checkbox column - always visible for selection */}
                        <th className="px-3 py-2 w-10">
                          <input
                            type="checkbox"
                            checked={selectedItems.size === lineItems.length && lineItems.length > 0}
                            onChange={toggleSelectAll}
                            className="rounded border-slate-300"
                          />
                        </th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700 w-28">Category</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700">Description</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700 w-20">Qty</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700 w-24">Unit</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700 w-28">Rate (R)</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700 w-28">Amount (R)</th>
                        <th className="text-left px-3 py-2 text-sm font-medium text-slate-700 w-20">Status</th>
                        {canEdit && isEditable && <th className="w-10"></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {lineItems.map((item, index) => {
                        const isRequisitioned = item.requisitioned;
                        const isLinkedToTask = item.task_id;
                        const isRowDisabled = isRequisitioned;
                        
                        return (
                        <tr 
                          key={item.id} 
                          className={`
                            ${isRequisitioned ? 'bg-slate-100 opacity-60' : 'hover:bg-slate-50'}
                            ${item.specifications === 'Descriptive item' ? 'bg-slate-50' : ''} 
                            ${selectedItems.has(item.id) && !isRequisitioned ? 'bg-blue-50' : ''}
                          `}
                        >
                          {/* Checkbox - disabled for requisitioned items */}
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(item.id)}
                              onChange={() => !isRequisitioned && toggleSelectItem(item.id)}
                              disabled={isRequisitioned}
                              className="rounded border-slate-300 disabled:opacity-50"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.category || 'Materials'}
                              onChange={(e) => updateLineItem(index, 'category', e.target.value)}
                              disabled={!canEdit || !isEditable || isRowDisabled}
                              className={`w-full px-2 py-1 border border-slate-200 rounded text-sm ${isRowDisabled ? 'bg-slate-100' : ''}`}
                            >
                              {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={item.item_description}
                                onChange={(e) => updateLineItem(index, 'item_description', e.target.value)}
                                disabled={!canEdit || !isEditable || isRowDisabled}
                                className={`flex-1 px-2 py-1 border border-slate-200 rounded text-sm ${isRowDisabled ? 'bg-slate-100' : ''}`}
                                placeholder="Item description"
                              />
                              {isLinkedToTask && (
                                <span className="flex items-center gap-1 text-xs text-blue-600 whitespace-nowrap" title={`Linked to: ${item.task_name || 'Task'}`}>
                                  <Link2 className="w-3 h-3" />
                                  {item.task_name?.slice(0, 15) || 'Task'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.quantity_str !== undefined ? item.quantity_str : item.quantity}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                updateLineItem(index, 'quantity_str', value);
                                updateLineItem(index, 'quantity', value ? parseFloat(value) : 0);
                              }}
                              disabled={!canEdit || !isEditable || isRowDisabled}
                              className={`w-full px-2 py-1 border border-slate-200 rounded text-sm ${isRowDisabled ? 'bg-slate-100' : ''}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.unit}
                              onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                              disabled={!canEdit || !isEditable || isRowDisabled}
                              className={`w-full px-2 py-1 border border-slate-200 rounded text-sm ${isRowDisabled ? 'bg-slate-100' : ''}`}
                            >
                              <option value="each">Each</option>
                              <option value="m">Meters (m)</option>
                              <option value="m2">Sq Meters (m²)</option>
                              <option value="m3">Cu Meters (m³)</option>
                              <option value="kg">Kilograms (kg)</option>
                              <option value="l">Litres (L)</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                              <option value="lot">Lot</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={item.rate_str !== undefined ? item.rate_str : item.rate}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9.]/g, '');
                                updateLineItem(index, 'rate_str', value);
                                updateLineItem(index, 'rate', value ? parseFloat(value) : 0);
                              }}
                              disabled={!canEdit || !isEditable || isRowDisabled}
                              className={`w-full px-2 py-1 border border-slate-200 rounded text-sm ${isRowDisabled ? 'bg-slate-100' : ''}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <span className={`font-medium ${isRowDisabled ? 'text-slate-500' : 'text-slate-900'}`}>
                              {formatCurrency(item.quantity * item.rate)}
                            </span>
                          </td>
                          {/* Status column */}
                          <td className="px-3 py-2">
                            {isRequisitioned ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                <Lock className="w-3 h-3" />
                                Requisitioned
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          {canEdit && isEditable && (
                            <td className="px-3 py-2">
                              {!isRowDisabled && (
                                <button
                                  onClick={() => removeLineItem(index)}
                                  className="p-1 hover:bg-red-50 rounded text-red-500"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                      <tr>
                        <td colSpan={6} className="px-3 py-3 text-right font-semibold text-slate-900">
                          Total:
                        </td>
                        <td className="px-3 py-3 font-bold text-lg text-slate-900">
                          {formatCurrency(calculateTotal())}
                        </td>
                        <td colSpan={canEdit && isEditable ? 2 : 1}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Line Items</span>
                <span className="font-medium text-slate-900">{lineItems.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Total Quantity</span>
                <span className="font-medium text-slate-900">
                  {lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0).toFixed(2)}
                </span>
              </div>
              <div className="pt-3 border-t border-slate-200 flex justify-between">
                <span className="font-semibold text-slate-700">Grand Total</span>
                <span className="font-bold text-lg text-blue-600">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>

          {/* Category Breakdown */}
          {Object.keys(categoryTotals).length > 0 && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">By Category</h2>
              <div className="space-y-2">
                {Object.entries(categoryTotals).map(([category, total]) => (
                  <div key={category} className="flex justify-between text-sm">
                    <span className="text-slate-600">{category}</span>
                    <span className="font-medium text-slate-900">{formatCurrency(total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status & Approval */}
          {existingBOQ && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">BOQ Status</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {(() => {
                    const config = statusConfig[existingBOQ.status] || statusConfig.draft;
                    const Icon = config.icon;
                    return (
                      <>
                        <Icon className={`w-5 h-5 ${existingBOQ.status === 'approved' ? 'text-green-600' : existingBOQ.status === 'rejected' ? 'text-red-600' : existingBOQ.status === 'pending_approval' ? 'text-yellow-600' : 'text-slate-600'}`} />
                        <span className={`font-medium ${existingBOQ.status === 'approved' ? 'text-green-600' : existingBOQ.status === 'rejected' ? 'text-red-600' : existingBOQ.status === 'pending_approval' ? 'text-yellow-600' : 'text-slate-600'}`}>
                          {config.label}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <p className="text-sm text-slate-600">
                  Created: {format(new Date(existingBOQ.created_at), 'MMM dd, yyyy')}
                </p>
                {existingBOQ.approved_by && approverName && (
                  <p className="text-sm text-slate-600">
                    Approved by: {approverName}
                  </p>
                )}
                {existingBOQ.approved_at && (
                  <p className="text-sm text-slate-600">
                    Approved: {format(new Date(existingBOQ.approved_at), 'MMM dd, yyyy')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          {canEdit && existingBOQ && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Actions</h2>
              <div className="space-y-2">
                {/* Submit for Approval (only for draft status) */}
                {existingBOQ.status === 'draft' && lineItems.length > 0 && (
                  <button
                    onClick={handleSubmitForApproval}
                    className="w-full py-2 px-4 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Submit for Approval
                  </button>
                )}

                {/* Resubmit (for rejected status) */}
                {existingBOQ.status === 'rejected' && lineItems.length > 0 && (
                  <button
                    onClick={handleSubmitForApproval}
                    className="w-full py-2 px-4 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Resubmit for Approval
                  </button>
                )}

                {/* Approve/Reject (for pending_approval status) */}
                {existingBOQ.status === 'pending_approval' && canApprove && (
                  <>
                    <button
                      onClick={() => handleApprove(true)}
                      className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Approve BOQ
                    </button>
                    <button
                      onClick={() => setShowApprovalModal(true)}
                      className="w-full py-2 px-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject BOQ
                    </button>
                  </>
                )}

                {/* Generate Requisition (only for approved status) */}
                {existingBOQ.status === 'approved' && (
                  <button
                    onClick={handleGenerateRequisition}
                    className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Generate Requisition
                  </button>
                )}

                {/* View Requisitions */}
                <button
                  onClick={() => navigate(`/requisitions?jobId=${selectedJob}`)}
                  className="w-full py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  View Requisitions
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Reject BOQ
              </h2>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Please provide a reason for rejecting this BOQ. This will be sent to the creator.
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason for rejection..."
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => { setShowApprovalModal(false); setRejectionReason(''); }}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApprove(false)}
                  disabled={!rejectionReason.trim()}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Reject BOQ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BOQManagement;
